import { motion } from "framer-motion";
import { Phone, MessageCircle, MapPin, Mail, Shield, Award, Users } from "lucide-react";
import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function ContactCTA({ content }) {
    const [form, setForm] = useState({ name: "", phone: "", message: "", service: "General" });
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.phone) {
            toast.error("Please add your name and phone.");
            return;
        }
        setLoading(true);
        try {
            await api.post("/leads", form);
            toast.success("Estimate request received. We'll call you shortly.");
            setForm({ name: "", phone: "", message: "", service: "General" });
        } catch (_e) {
            toast.error("Could not send. Please call us directly.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section id="contact" data-testid="contact-section" className="relative bg-[color:var(--bg-primary)] pt-24 md:pt-32 pb-16">
            <div className="mx-auto max-w-[1440px] px-6 md:px-10">
                <div className="relative overflow-hidden bg-[color:var(--surface-dark)] text-white p-8 md:p-16 lg:p-20 rounded-[2px]">
                    <div className="absolute inset-0 grid-bg-dark pointer-events-none" />
                    <div className="absolute -top-40 -left-40 h-[400px] w-[400px] rounded-full bg-[color:var(--brand-blue)]/20 blur-3xl pointer-events-none" />

                    <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
                        {/* Left: massive CTA */}
                        <div>
                            <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/50">[ Free Estimate — 05 ]</div>
                            <h2 className="mt-6 font-display font-black tracking-tighter leading-[0.85] text-[12vw] md:text-[6.5vw] lg:text-[5vw]">
                                CALL OR TEXT<br />
                                <span className="text-[color:var(--brand-blue)]">TODAY.</span>
                            </h2>

                            <a
                                href={`tel:${content.phone_raw}`}
                                data-testid="cta-phone-btn"
                                className="mt-10 inline-flex items-center gap-4 group"
                            >
                                <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[color:var(--brand-blue)] group-hover:scale-110 transition-transform">
                                    <Phone className="h-6 w-6" />
                                </span>
                                <span className="font-display text-3xl md:text-5xl font-bold tracking-tighter text-white">
                                    {content.phone}
                                </span>
                            </a>

                            <div className="mt-6 flex flex-wrap items-center gap-3">
                                <a
                                    href={`https://wa.me/${content.whatsapp}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 backdrop-blur px-5 py-3 text-sm font-semibold hover:bg-white/20 transition-colors"
                                >
                                    <MessageCircle className="h-4 w-4" /> WhatsApp
                                </a>
                                {content.email && (
                                    <a
                                        href={`mailto:${content.email}`}
                                        data-testid="cta-email-btn"
                                        className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 backdrop-blur px-5 py-3 text-sm font-semibold hover:bg-white/20 transition-colors break-all"
                                    >
                                        <Mail className="h-4 w-4" /> Email us
                                    </a>
                                )}
                                <div className="text-xs text-white/60 flex items-center gap-2">
                                    <MapPin className="h-3.5 w-3.5" /> {content.service_area}
                                </div>
                            </div>

                            <div className="mt-14 grid grid-cols-3 gap-4">
                                {[
                                    { Icon: Award, label: "Quality Work" },
                                    { Icon: Users, label: "Customer Satisfaction" },
                                    { Icon: Shield, label: "Licensed & Insured" },
                                ].map(({ Icon, label }) => (
                                    <div key={label} className="border-t border-white/15 pt-4">
                                        <Icon className="h-5 w-5 text-[color:var(--brand-blue)]" />
                                        <div className="mt-3 text-[11px] uppercase tracking-[0.2em] text-white/80 leading-tight">{label}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Right: form */}
                        <motion.form
                            onSubmit={submit}
                            data-testid="lead-form"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                            className="border border-white/10 bg-white/[0.04] backdrop-blur-md p-6 md:p-10"
                        >
                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-6">Request an Estimate</div>
                            <div className="space-y-5">
                                <label className="block">
                                    <span className="text-xs uppercase tracking-[0.2em] text-white/70">Name</span>
                                    <input
                                        data-testid="lead-name"
                                        value={form.name}
                                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                                        className="mt-2 w-full bg-transparent border-b border-white/30 pb-2 text-white outline-none focus:border-[color:var(--brand-blue)] transition-colors"
                                        placeholder="Your full name"
                                    />
                                </label>
                                <label className="block">
                                    <span className="text-xs uppercase tracking-[0.2em] text-white/70">Phone</span>
                                    <input
                                        data-testid="lead-phone"
                                        value={form.phone}
                                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                        className="mt-2 w-full bg-transparent border-b border-white/30 pb-2 text-white outline-none focus:border-[color:var(--brand-blue)] transition-colors"
                                        placeholder="(225) 000-0000"
                                    />
                                </label>
                                <label className="block">
                                    <span className="text-xs uppercase tracking-[0.2em] text-white/70">Service</span>
                                    <select
                                        data-testid="lead-service"
                                        value={form.service}
                                        onChange={(e) => setForm({ ...form, service: e.target.value })}
                                        className="mt-2 w-full bg-transparent border-b border-white/30 pb-2 text-white outline-none focus:border-[color:var(--brand-blue)] transition-colors [&>option]:text-neutral-900"
                                    >
                                        <option>General</option>
                                        <option>Painting</option>
                                        <option>Construction</option>
                                        <option>Remodeling</option>
                                        <option>Roofing</option>
                                        <option>Flooring / Tile</option>
                                    </select>
                                </label>
                                <label className="block">
                                    <span className="text-xs uppercase tracking-[0.2em] text-white/70">Project details (optional)</span>
                                    <textarea
                                        data-testid="lead-message"
                                        value={form.message}
                                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                                        rows={3}
                                        className="mt-2 w-full bg-transparent border-b border-white/30 pb-2 text-white outline-none focus:border-[color:var(--brand-blue)] transition-colors resize-none"
                                        placeholder="Tell us about your project…"
                                    />
                                </label>
                            </div>
                            <button
                                type="submit"
                                data-testid="lead-submit"
                                disabled={loading}
                                className="btn-brand mt-8 inline-flex items-center gap-3 rounded-full bg-[color:var(--brand-blue)] px-7 py-3.5 text-sm font-semibold text-white uppercase tracking-wider disabled:opacity-60"
                            >
                                {loading ? "Sending…" : "Send Request"}
                            </button>
                        </motion.form>
                    </div>
                </div>
            </div>
        </section>
    );
}
